import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { useAuth } from "@/hooks/useAuth";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ScheduledTransferEntity } from "./schema/scheduled-transfer.schema";
import { AppRoutes } from "@/constants/routes";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { formatCurrency } from "@/utils/format-currency";
import dayjs from "dayjs";
import { ScheduledTransferStatus } from "@/constants/entity-status";
import { NoData } from "@/components/common/section-components/NoData";
import { StandardTable } from "@/components/table/StandardTable";
import { scheduledTransferRequestHeaderElements } from "./schema/header-elements";
import { ScheduledTransferRequestActions } from "@/components/common/section-components/action/ScheduledTransferRequestActions";
import { usePolling } from "@/hooks/usePolling";
import { TransferRequestFilter } from "@/components/common/section-components/filters/TransferRequestFilter";
import {
  clearFiltersHandler,
  submitFiltersHandler,
} from "../transfer/schema/handler-functions";
import { useRoles } from "@/hooks/useRoles";
import { fetchScheduledTransferRequestsHandler } from "./schema/handler-functions";

export const ScheduledTransferPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();

  const { handleSubmit, setValue, getValues, control } = useForm();

  const [
    loadingScheduledTransferRequests,
    setLoadingScheduledTransferRequests,
  ] = useState<boolean>(false);

  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const [scheduledTransferRequests, setScheduledTransferRequests] = useState<{
    listado: ScheduledTransferEntity[];
    totalPages: number;
    totalRecords: number;
    pendingRequests: boolean;
  }>({
    listado: [],
    pendingRequests: false,
    totalPages: 1,
    totalRecords: 10,
  });

  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const navigate = useNavigate();

  const fetchScheduledTransferRequests = async (
    filters?: any,
    showLoad = true,
  ) => {
    await fetchScheduledTransferRequestsHandler({
      showLoad,
      setLoadingScheduledTransferRequests,
      setScheduledTransferRequests,
      filters,
      user,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchScheduledTransferRequests();
    }
  }, [isActive]);

  const [page, setPage] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({});

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      setOpenFilters
    });
  };

  const { fetchPermissions } = useRoles();

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    submitFiltersHandler({
      event,
      fetchTransferRequests: fetchScheduledTransferRequests,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      navigate,
      fetchPermissions,
      scheduled: true,
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchScheduledTransferRequests({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.SCHEDULED_TRANSFER_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };
  const getActionElements = (item: any) => (
    <ScheduledTransferRequestActions handleDetail={handleDetail} item={item} />
  );

  usePolling({
    callback: () =>
      fetchScheduledTransferRequests(
        { ...currentFilters, "P.Pagina": page },
        false,
      ),
    delay: 10000,
    isActive: isActive && !loadingScheduledTransferRequests,
  });

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Transferencias programadas"
        loading={loadingScheduledTransferRequests}
      />

      <TransferRequestFilter
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        clearFilters={clearFilters}
        filtersApplied={filtersApplied}
        submitFilters={submitFilters}
        control={control}
        disableFilters={loadingScheduledTransferRequests}
        scheduled
      />

      <Flex flexDir="column">
        {Boolean(
          !loadingScheduledTransferRequests &&
            !scheduledTransferRequests?.listado?.length,
        ) && <NoData />}
        {Boolean(
          !loadingScheduledTransferRequests &&
            scheduledTransferRequests?.listado?.length,
        ) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                scheduledTransferRequests?.listado?.map(
                  (el: ScheduledTransferEntity) => ({
                    ...el,
                    fechaEjecucion: dayjs(el?.fechaEjecucion).format(
                      "DD/MM/YYYY",
                    ),
                    monto: `$${formatCurrency(el?.monto)}`,
                    displayEstado: (
                      <RequestStatusBadge
                        status={
                          el?.estadoDescripcion as ScheduledTransferStatus
                        }
                      />
                    ),
                  }),
                ) ?? []
              }
              headers={scheduledTransferRequestHeaderElements}
              actionElements={getActionElements}
            />
            {scheduledTransferRequests?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={scheduledTransferRequests?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
